// i would probably rewrite this to use '@use-gesture/vanilla' and 'juliangarnier/anime'

import { html, LitElement, TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

type point = [number,number];

@customElement('carousel-element')
export class CarouselElement extends LitElement {

	@property({ type: Number, attribute: 'entry-count' })
	entryCount: number = 0;

	@property({ type: Boolean, reflect: true })
	dragging: boolean = false;

	@property({ type: Boolean, reflect: true })
	wheel: boolean = false;

	@property({ type: Boolean, reflect: true })
	transition: boolean = true;

	@state()
	_columnCount: number = 4; // set via CSS var

	@state()
    _body: string = '';

	@state()
    _position: number = 0;

	@state()
    _offset: number = 0;

	@state()
    _dragRef?: point;

	@state()
    _dragOrigin?: point;

	@state()
    _dragDelta?: point;

	@state()
    _prevDragDelta?: point;

	@query('.Carousel-container')
	_containerElement!: HTMLElement;

	@query('.Carousel-container > *')
	_bodyElement!: HTMLElement;

	wheelTimeout: number|null = null;


	// Lifecycle Hooks

    firstUpdated() {

        // class list
        this.classList.add('Carousel');
		this.style.opacity = '0';

		// body slot
        const bodySlot: Element|null = this.querySelector('[slot="body"]');
        this._body = bodySlot!.innerHTML;
        bodySlot!.remove();
	
		setTimeout(() => {
			this.setOffset();
			setTimeout(() => {
				this.transition = false;
				this.style.opacity = '1';
				this.transition = true;
			}, 250);
		}, 100);

		this._containerElement.addEventListener('mousedown', (e: MouseEvent) => {
			this.dragging = true;
			this.transition = false;
			this._dragRef = [e.x, e.y];
			this._dragOrigin = [e.x, e.y];
		});

		this._containerElement.addEventListener('touchstart', (e: TouchEvent) => {
			this.dragging = true;
			this.transition = false;
			this._dragRef = [e.touches[0].screenX, e.touches[0].screenY];
			this._dragOrigin = [e.touches[0].screenX, e.touches[0].screenY];
		});

		window.addEventListener('mousemove', (e: MouseEvent) => {
			if (this.dragging && this._dragRef) {
				this._position -= this._dragRef[0] - e.x;
				this._dragRef = [e.x, e.y];
				if (this.leftExtent) this.prependCopy();
				if (this.rightExtent) this.appendCopy();
			}
		});

		this._containerElement.addEventListener('touchmove', (e: TouchEvent) => {
			if (this.dragging && this._dragRef) {
				this._dragDelta = [
					this._dragRef[0] - e.touches[0].screenX, 
					this._dragRef[1] - e.touches[0].screenY
				];
				this.dragging = Math.abs(this._dragDelta[0]) > Math.abs(this._dragDelta[1]);
			}
			if (this.dragging && this._dragRef) {
				e.preventDefault();
				this._position -= (this._dragRef[0] - e.touches[0].screenX);
				this._dragRef = [e.touches[0].screenX, e.touches[0].screenY];
				if (this.leftExtent) this.prependCopy();
				if (this.rightExtent) this.appendCopy();
			}
		});

		this.addEventListener('wheel', (e: WheelEvent) => {	
			
			// horizontal mouse
			if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {

				// get current delta
				this._dragDelta = [e.deltaX, e.deltaY];

				// positive inertia
				if ( !this._prevDragDelta || Math.abs(this._dragDelta[0]) > Math.abs(this._prevDragDelta[0]) ) this.wheel = true;

				// reset wheel timeout
				if (this.wheelTimeout) clearTimeout(this.wheelTimeout);
				this.wheelTimeout = setTimeout(() => { 
					this.wheel = false;
					this.transition = true;
					this._dragRef = undefined;
					this.snapToNearest(); 
				}, 250);

				// handle movement
				if (this.wheel) {
					this._dragRef = [0,0];
					this._position -= (this._dragRef[0] + this._dragDelta[0]);
					if (this.leftExtent) this.prependCopy();
					if (this.rightExtent) this.appendCopy();
				}

				// get prev delta
				this._prevDragDelta = [e.deltaX, e.deltaY];

			}
		});

		window.addEventListener('mouseup', (e: MouseEvent) => {
			if (this._dragOrigin && this._dragRef) {

				// get origin values
				const originDeltaX = Math.abs(this._dragOrigin[0] - e.clientX);
				const originDeltaY = Math.abs(this._dragOrigin[1] - e.clientY);

				// handle click
				if (originDeltaX <= 10 && originDeltaY <= 10) {
					const clickedElement = document.elementFromPoint(e.clientX, e.clientY);
					if (clickedElement) {
						const link = clickedElement.querySelector('a');
						if (link) window.location.href = link.href;
					}
				}

				// reset state
				this.dragging = false;
				this.transition = true;
				this._dragRef = undefined;
				this._dragOrigin = undefined;

				// snap to nearest
				this.snapToNearest();
			}

			this._containerElement.addEventListener('focusin', (e: FocusEvent) => {
				const target = e.target as HTMLElement;
				const targetBbox = target.getBoundingClientRect();
				const carouselBbox = this.getBoundingClientRect();
				const containerBbox = this._containerElement.getBoundingClientRect();

				const targetLeft = targetBbox.left - carouselBbox.left;
				const targetRight = targetBbox.right - carouselBbox.left;

				if (targetLeft < 0 || targetRight > carouselBbox.width) {
					this.transition = true;
					// Calculate where target is relative to container origin
					const targetOffsetLeft = targetBbox.left - containerBbox.left;
					// Snap position so target aligns to nearest thumbnail
					this._position = -(Math.round(targetOffsetLeft / this.thumbnailWidth) * this.thumbnailWidth);
					this.snapToNearest();
				}
			});
						
		});

		window.addEventListener('touchend', (e: TouchEvent) => {
			if (this._dragOrigin && this._dragRef) {

				// get origin values
				const originDeltaX = Math.abs(this._dragOrigin[0] - e.changedTouches[0].clientX);
				const originDeltaY = Math.abs(this._dragOrigin[1] - e.changedTouches[0].clientY);

				// handle click
				if (originDeltaX <= 10 && originDeltaY <= 10) {
					const clickedElement = document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
					if (clickedElement) {
						const link = clickedElement.querySelector('a');
						if (link) window.location.href = link.href;
					}
				}

				// reset state
				this.dragging = false;
				this.transition = true;
				this._dragRef = undefined;
				this._dragOrigin = undefined;

				// snap to nearest
				if (this._dragDelta && this._dragDelta[0]) {
					const offset = (this._dragDelta[0] > 0)
						? -1
						: 1;
					this.snapToNearest(offset);
					this._dragDelta = [0,0];
				}

			}
		});

		this._containerElement.addEventListener('transitionend', () => {
			if (!this.dragging) {
				if (this.leftExtent) this.prependCopy();
				if (this.rightExtent) this.appendCopy();
			}
		});

		window.addEventListener('resize', () => {
			this.transition = false;
			this.setOffset();
			this.snapToNearest();
			this.transition = true;
		});

	}

	// Render

    render() {
        return html`
			${ this.navTemplate() }
			<div class="Carousel-container" style="left: ${ this._position + this._offset }px">
            	${ unsafeHTML(this._body) }
			</div>
        `;
    }

	createRenderRoot() { 
        return this;
    }

	// Templates

	navTemplate(): TemplateResult {
		return html`
			<nav class="Carousel-nav">
				<div class="Carousel-navItem Carousel-navItem--prev">
					<button-input
						name="scroller"
						@click="${ this.moveBack }">
						<div slot="label">
							←
						</div>
					</button-input>
				</div>
				<div class="Carousel-navItem Carousel-navItem--next">
					<button-input
						name="scroller"
						@click="${ this.moveForwards }">
						<div slot="label">
							→
						</div>
					</button-input>
				</div>
			</nav>
		`;
	}

	// Methods

	setOffset() {
		this._offset = ((this.width - this.thumbnailWidth) / 2) - this.thumbnailWidth;
	}

	snapToNearest(offset: number = 0) {
		this._position = Math.round(this._position / this.thumbnailWidth) * this.thumbnailWidth + (this.thumbnailWidth * offset);
	}

	moveBack() {
		if (this.leftExtent) this.prependCopy();	
		setTimeout(() => { 
			this.transition = true; 
			this._position += this.thumbnailWidth;
		}, 100);
	}

	moveForwards() {
		if (this.rightExtent) this.appendCopy();	
		setTimeout(() => { 
			this.transition = true; 
			this._position -= this.thumbnailWidth;
		}, 100);
	}

	prependCopy() {
		this.transition = false;
		this._bodyElement.insertBefore(
			this._bodyElement.lastElementChild!, 
			this._bodyElement.firstElementChild
		);
		this._position -= this.thumbnailWidth;
	}

	appendCopy() {
		this.transition = false;
		this._bodyElement.appendChild(
			this._bodyElement.firstElementChild!
		);
		this._position += this.thumbnailWidth;
	}

	// Getters

	get width() {
		const bbox = this.getBoundingClientRect()
		return bbox.right - bbox.left;
	}

	get containerWidth() {
		const bbox = this._containerElement.getBoundingClientRect()
		return bbox.right - bbox.left;
	}

	get thumbnailWidth() {
		return this.containerWidth/this.entryCount;
	}

	get columnCount() {
		const styleDef = Number(getComputedStyle(this._containerElement.firstElementChild!).getPropertyValue('--column-count'))
			|| Number(getComputedStyle(this).getPropertyValue('--column-count'));
		const columnCount = (styleDef) ? styleDef : String(this._columnCount);
		return Number(columnCount);
	}

	get leftExtent() {
		return this._position + this._offset + this.thumbnailWidth >= 0;
	}

	get rightExtent() {
		return this.containerWidth + this._position + this._offset - this.thumbnailWidth <= window.innerWidth;
	}

	get activeIndex() {
		return this._position / this.columnCount
	}
}